import React, { useEffect, useRef, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { Accordion, ProgressBar } from 'react-bootstrap';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { useMutationObserver } from 'rooks';
import { useTranslation } from 'react-i18next';
import { AccordionSelectCallback } from 'react-bootstrap/esm/AccordionContext';

import type { IContractRequirement, IScheduleSummary, IUnmetContractRequirement } from '@/store/types';
import type { IWeekBlocks } from '@/types/block';

import { useAppDispatch, useAppSelector, useWeek } from '@/hooks';
import { flex } from '@/styles/theme';
import { ContractRequirementStatus, RequirementType } from '@/store/enums';
import { classList, findUnmetRequirementsByReqType } from '@/helpers';
import {
  AccordionTitle,
  ScheduleDot,
  ScheduleDots,
  StyledAccordion,
  StyledAccordionItem,
  StyledChildAccordion,
  Weeks,
} from './ContractRequirementsWidget.styled';
import { colors } from '@/styles/colors';
import {
  selectIsSchedulesListLoading,
  selectScheduleColors,
  selectSchedulesList,
} from '@/store/selectors/scheduleSelectors';
import {
  getContractAdherence,
  getContractRequirementsAll,
  getContractRequirementsUnmet,
} from '@/store/slices/contractSlice';
import {
  selectAgentContracts,
  selectContractAdherence,
  selectContractRequirementsAll,
  selectContractRequirementsUnmet,
  selectIsPendingAction,
} from '@/store/selectors/contractSelectors';
import { Sidebar } from './Sidebar';
import { LoaderWrapper } from './loaders';
import { ContractRequirementsWidgetSkeleton } from './ContractRequirementsWidget.skeleton';
import { selectAgentContractClients } from '@/store/selectors/authSelectors';
import AccordionCustomToggle from '@/components/common/AccordionCustomToggle';

interface IAccordionItem {
  contractRequirement: IContractRequirement;
  unmetRequirements?: IUnmetContractRequirement[];
  eventKey: string;
  children?: React.ReactNode;
  weekId: number;
}

interface IAccordionChildItem {
  title: string;
  eventKey: string;
  options?: {
    now: number;
    max: number;
  };
  workSchedules: { weekday: string; hours: string }[];
  weeklyHours: number;
  disableCollapse: boolean;
  schedules: IScheduleSummary[];
  contractId: number;
  weekId: number;
  requirementType: string;
}

const childAccordionSelector = createSelector(
  selectScheduleColors,
  selectContractAdherence,
  (scheduleColors, contractAdherence) => ({
    scheduleColors,
    contractAdherence,
  }),
);

const ChildAccordion = ({
  title,
  eventKey,
  options,
  workSchedules,
  weeklyHours,
  disableCollapse,
  schedules,
  contractId,
  weekId,
  requirementType,
}: IAccordionChildItem): React.ReactElement => {
  const { scheduleColors, contractAdherence } = useAppSelector(childAccordionSelector);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [actualHours, setActualHours] = useState(0);
  const [variant, setVariant] = useState<ContractRequirementStatus>(ContractRequirementStatus.Danger);
  const decorateOnClick = useAccordionButton(eventKey, () => {
    setIsActive(!isActive);
  });
  const uniqueScheduleIds = [...new Set(schedules.map(item => item.scheduleId))];

  useEffect(() => {
    const { actualBlocks } =
      contractAdherence[contractId]?.find(
        item =>
          item.contractId === contractId &&
          item.weekId === weekId &&
          item.typeName.toLowerCase() === requirementType.toLowerCase(),
      ) || {};
    actualBlocks !== undefined && setActualHours(actualBlocks / 2);
  }, [contractAdherence, contractId, requirementType, weekId]);

  useEffect(() => {
    if (actualHours >= weeklyHours) {
      setVariant(ContractRequirementStatus.Success);
    } else {
      setVariant(ContractRequirementStatus.Danger);
    }
  }, [actualHours, weeklyHours]);

  return (
    <StyledChildAccordion>
      <div onClick={decorateOnClick}>
        <AccordionCustomToggle
          as={AccordionTitle}
          eventKey={eventKey}
          className={classList(isActive && 'active', disableCollapse && 'disable-collapse')}
        >
          <>
            {scheduleColors && (
              <ScheduleDots>
                {uniqueScheduleIds.map(scheduleId => {
                  if (scheduleColors?.[scheduleId] && colors?.[scheduleColors[scheduleId]]?.['c500'])
                    return <ScheduleDot key={scheduleId} color={colors[scheduleColors[scheduleId]]['c500']} />;
                  return <></>;
                })}
              </ScheduleDots>
            )}
            <div>{title}</div>
            <div className={`progress-container progress-variant-${variant}`}>
              {options && `${actualHours}/${options.max}h`}
              <ProgressBar max={options?.max || 0} now={actualHours} variant={variant} />
            </div>
          </>
        </AccordionCustomToggle>
      </div>
      {!disableCollapse && (
        <Accordion.Collapse eventKey={eventKey}>
          <Weeks className={flex.column}>
            {workSchedules.map((workSchedule, index) => (
              <div key={index}>
                <span>{workSchedule.weekday}</span>
                <span>{workSchedule.hours}</span>
              </div>
            ))}
          </Weeks>
        </Accordion.Collapse>
      )}
    </StyledChildAccordion>
  );
};

const accordionItemSelector = createSelector(selectContractRequirementsUnmet, contractRequirementsUnmet => ({
  contractRequirementsUnmet,
}));

const AccordionItem = ({
  contractRequirement,
  unmetRequirements,
  eventKey,
  weekId,
}: IAccordionItem): React.ReactElement => {
  const { contractRequirements } = contractRequirement;
  const refAccordion = useRef<HTMLDivElement>(null);
  const [contractVariant, setContractVariant] = useState<ContractRequirementStatus>(ContractRequirementStatus.Danger);
  const dispatch = useAppDispatch();
  const { contractRequirementsUnmet } = useAppSelector(accordionItemSelector);

  useMutationObserver(refAccordion, () => {
    if (refAccordion.current?.querySelector('.progress-variant-danger') === null) {
      setContractVariant(ContractRequirementStatus.Success);
    } else {
      setContractVariant(ContractRequirementStatus.Danger);
    }
  });

  useEffect(() => {
    const contractId = contractRequirements[0]?.contractId;
    if (!contractId) return;

    const lookbackWeeks = 0;
    const getContractAdherenceCall = dispatch(getContractAdherence({ contractId, weekId, lookbackWeeks }));

    return () => getContractAdherenceCall.abort();
  }, [contractRequirementsUnmet, weekId, dispatch, contractRequirements]);

  return (
    <StyledAccordionItem data-key={eventKey}>
      <AccordionCustomToggle
        as={AccordionTitle}
        eventKey={eventKey}
        className={classList('gigname', contractVariant === ContractRequirementStatus.Success && 'met')}
      >
        {contractRequirement.gigName}
      </AccordionCustomToggle>
      <Accordion.Collapse eventKey={eventKey}>
        <Accordion ref={refAccordion}>
          {contractRequirements.map((item, index) => (
            <ChildAccordion
              key={`${item.contractId}${item.requirementType}_${index}`}
              title={item.requirementType}
              eventKey={String(index)}
              options={
                findUnmetRequirementsByReqType(item.requirementType, unmetRequirements)?.unmet || {
                  now: 0,
                  max: item.weeklyHours,
                }
              }
              workSchedules={item.workSchedules}
              weeklyHours={item.weeklyHours}
              disableCollapse={item.requirementType === RequirementType.min}
              schedules={item.contractRequirementScheduleSummary}
              weekId={weekId}
              contractId={item.contractId}
              requirementType={item.requirementType}
            />
          ))}
        </Accordion>
      </Accordion.Collapse>
    </StyledAccordionItem>
  );
};

const contractRequirementsWidgetSelector = createSelector(
  selectContractRequirementsAll,
  selectContractRequirementsUnmet,
  selectAgentContractClients,
  selectAgentContracts,
  selectSchedulesList,
  selectIsPendingAction(getContractRequirementsAll.typePrefix),
  selectIsPendingAction(getContractRequirementsUnmet.typePrefix),
  selectIsSchedulesListLoading,
  (
    contractRequirementsAll,
    contractRequirementsUnmet,
    agentContractClients,
    agentContracts,
    schedulesList,
    isContractRequirementsAllLoading,
    isContractRequirementsUnmetLoading,
    isSchedulesListLoading,
  ) => ({
    contractRequirementsAll:
      contractRequirementsAll?.filter(
        contract =>
          agentContracts.some(agentContract => agentContract.contractId === contract.contractId) &&
          agentContractClients?.some(client => client.clientId === contract.clientSummary.clientId),
      ) ?? [],
    contractRequirementsUnmet,
    schedulesList,
    isLoading: isContractRequirementsAllLoading || isContractRequirementsUnmetLoading || isSchedulesListLoading,
  }),
);

const ContractRequirementsWidget = ({ weekBlocks }: { weekBlocks: IWeekBlocks }): React.ReactElement => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState<string | string[] | undefined>();
  const { selectedWeekId } = useWeek();
  const { contractRequirementsAll, contractRequirementsUnmet, schedulesList, isLoading } = useAppSelector(
    contractRequirementsWidgetSelector,
  );

  const handleSelect: AccordionSelectCallback = key => {
    setActiveKey(key || undefined);
  };

  return (
    <LoaderWrapper isLoading={isLoading} loader={<ContractRequirementsWidgetSkeleton />}>
      <Sidebar.Item name={t`commonComponents.gigRequirements`}>
        <StyledAccordion onSelect={handleSelect} activeKey={activeKey}>
          {contractRequirementsAll
            // filter contracts for schedules available for current Agent
            .filter(contractRequirement =>
              contractRequirement.contractRequirements.some(
                item =>
                  item.startBlock &&
                  item.startBlock <= weekBlocks.startBlock &&
                  (!item.endBlock || item.endBlock <= weekBlocks.endBlock) &&
                  item.contractRequirementScheduleSummary.some(schedule =>
                    schedulesList.some(obj => obj.scheduleId === schedule.scheduleId),
                  ),
              ),
            )
            .map((contractRequirement, index) => {
              const unmetRequirements = contractRequirementsUnmet.filter(
                item => item.contractId === contractRequirement.contractId && item.weekId === selectedWeekId,
              );

              return (
                <AccordionItem
                  key={contractRequirement.contractId}
                  contractRequirement={contractRequirement}
                  unmetRequirements={unmetRequirements}
                  eventKey={String(index)}
                  weekId={selectedWeekId}
                />
              );
            })}
        </StyledAccordion>
      </Sidebar.Item>
    </LoaderWrapper>
  );
};

export { ContractRequirementsWidget };
